import { Graphics } from 'pixi.js';
import { Boss } from './boss-base';
import { getTexture } from '@/assets/resolver';
import { COLORS } from '@/config/colors';
import {
  BODY_DMG_MUL, CENTIPEDE, HEAD_DMG_MUL, HEAD_RADIUS, SEG_COUNT, SEG_RADIUS,
  SHORT_SPEED_BONUS, SIDE_FIRE_INTERVAL, GROW_HP_MUL, GROW_INTERVAL,
  buildInitialNodes, fireChain, growTail, nearestCore, nodeInBlast, type Chain, type Node,
} from './centipede-parts';
import { BURROW, stepChain } from './centipede-move';
import { BOSS_MECHANICS } from '@/config/boss-mechanics';

const ANTI_STALL = BOSS_MECHANICS.centipede;
export { CENTIPEDE } from './centipede-parts';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';

/**
 * I1 — Centipede (magma flavor). A long armored chain: the HEAD
 * shrugs off damage, the BODY cores are soft. Killing a MIDDLE segment
 * SPLITS the chain — both halves grow a head and keep fighting (and faster,
 * being shorter). Splitting cleanly is the smart play, but mid segments
 * carry extra HP, so it is earned. It BURROWS instead of lunging: a dust
 * mound marks where it erupts (centipede-move.ts). docs/02 + docs/12 I1.
 */
export class CentipedeBoss extends Boss {
  private chains: Chain[] = [];
  private readonly totalNodes = SEG_COUNT + 1;
  private growTimer: number = GROW_INTERVAL;
  private regrown = 0;
  private time = 0;
  private readonly dirt = new Graphics();

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(CENTIPEDE, level, x, y, homeX, homeY);
    this.body.visible = false; // fully custom rendering
    this.view.addChildAt(this.dirt, 0); // mounds under the chain

    this.chains = [this.makeChain(buildInitialNodes(this.view, x, y, this.maxHp), 0, 1)];
    this.syncHp();
    this.runners = this.makeRunners([], true); // tier evolve layers (A3)
  }

  /** Node HP was split from the unscaled max — rescale every node (X1). */
  protected override scaleParts(mul: number): void {
    for (const c of this.chains) for (const n of c.nodes) n.hp *= mul;
    this.syncHp();
  }

  private makeChain(nodes: Node[], phase: number, dir: 1 | -1): Chain {
    const head = nodes[0];
    if (head && !head.head) {
      head.head = true;
      head.sprite.texture = getTexture('boss/centipede/head', 0);
      head.sprite.scale.set(1);
    }
    return {
      nodes, vx: 0, vy: 0, phase, dir,
      state: 'surface', stateT: 0, burrowTimer: BURROW.interval * (BURROW.firstAt + phase * 0.2),
      tx: 0, ty: 0,
      headFire: 1.4 + phase * 0.4, sideFire: SIDE_FIRE_INTERVAL * (0.7 + phase * 0.15),
    };
  }

  /** Unrounded: the bar drops by exactly the numbers shown. */
  private syncHp(): void {
    this.hp = Math.max(
      0, this.chains.reduce((s, c) => s + c.nodes.reduce((n, node) => n + node.hp, 0), 0),
    );
    if (this.chains.length === 0) this.dead = true;
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    if (this.dead) return;
    this.time += dt;
    this.hitFlash -= dt;
    this.updateReinforce(dt);
    this.regrow(dt);
    for (const chain of this.chains) this.updateChain(chain, dt, px, py, bullets);
    const lead = this.chains[0]?.nodes[0];
    if (lead) {
      this.x = lead.x;
      this.y = lead.y;
    }
    this.fireRunners(dt, px, py, bullets, telegraphs);
  }

  /**
   * Severed pieces REGROW (docs/12 L4): ONE node per interval for the whole
   * boss, onto the shortest chain — never past the original length or the
   * full bar (X2), so splitting can't snowball into endless regrowth.
   */
  private regrow(dt: number): void {
    this.growTimer -= dt;
    if (this.growTimer > 0) return;
    this.growTimer = GROW_INTERVAL;
    const hp = (this.maxHp / this.totalNodes) * GROW_HP_MUL;
    let alive = 0;
    let shortest: Chain | null = null;
    for (const c of this.chains) {
      alive += c.nodes.length;
      if (!shortest || c.nodes.length < shortest.nodes.length) shortest = c;
    }
    // Finite (D7 stall): a budget per fight, and the last stand never heals
    if (
      this.regrown >= ANTI_STALL.regrowMax || alive <= ANTI_STALL.regrowMinNodes ||
      this.hpPct <= ANTI_STALL.regrowStopHpPct
    ) return;
    if (shortest && alive < this.totalNodes && this.hp + hp <= this.maxHp) {
      growTail(this.view, shortest, hp);
      this.regrown++;
      this.syncHp();
    }
  }

  private updateChain(c: Chain, dt: number, px: number, py: number, bullets: BulletSystem): void {
    // Shorter chains fire faster; their move bonus is capped (hittable, D7)
    const bonus = (1 - c.nodes.length / this.totalNodes) * SHORT_SPEED_BONUS;
    const f = (1 + Math.min(ANTI_STALL.shortMoveCap, bonus)) * this.levelSpeedMul;
    if (stepChain(c, dt, px, py, this.homeX, this.homeY, f)) this.erupt(c, bullets);
    if (c.state !== 'under') {
      fireChain(c, dt, (1 + bonus) * this.intensity, px, py, bullets, this.bulletSpeedMul);
    }
  }

  /** Bursting out of the mound: a magma ring from the head. */
  private erupt(c: Chain, bullets: BulletSystem): void {
    const head = c.nodes[0]!;
    const speed = BURROW.burstSpeed * this.bulletSpeedMul;
    for (let i = 0; i < BURROW.burst; i++) {
      const a = (Math.PI * 2 * i) / BURROW.burst;
      bullets.spawn({
        x: head.x, y: head.y, dirX: Math.cos(a), dirY: Math.sin(a), speed, life: 4,
        radius: 10, damage: 1, friendly: false, texKey: 'bullet/enemy/ball', rotates: false,
      });
    }
    this.events?.burst(head.x, head.y, BOSS_FEEDBACK.centipedeErupt);
  }

  /** Evolve layers fire from the lead head — never from underground. */
  protected override fireRunners(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    if (this.chains[0]?.state === 'under') return;
    super.fireRunners(dt, px, py, bullets, telegraphs);
  }

  /** Underground chains can't be hit; vulnerable while any chain is up. */
  override get isVulnerable(): boolean {
    return super.isVulnerable && this.chains.some((c) => c.state !== 'under');
  }

  // ---- Collision: every node killable; mid-kill SPLITS the chain ----

  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    let hits = 0;
    let dealt = 0;
    for (const chain of this.chains) {
      if (chain.state === 'under') continue; // shots pass over the tunnel
      for (const node of chain.nodes) {
        if (node.hp <= 0) continue;
        const r = node.head ? HEAD_RADIUS : SEG_RADIUS;
        const mul = node.head ? HEAD_DMG_MUL : BODY_DMG_MUL;
        hits += bullets.collideCircle(true, node.x, node.y, r, (dmg) => {
          this.hitFlash = 0.08;
          if (this.damageLocked) return 0; // reinforcement squad first (X9)
          const dealtHere = Math.min(Math.max(0, node.hp), dmg * mul);
          node.hp -= dmg * mul;
          dealt += dealtHere;
          return dealtHere; // armor-scaled, as this node really took it
        });
      }
    }
    this.processDeaths();
    return { hits, dealt };
  }

  override hitByAoe(x: number, y: number, radius: number): boolean {
    return nodeInBlast(this.chains, x, y, radius) !== null;
  }

  /** Blasts hurt the node they touch — head armor applies (X5). */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    if (this.dead || this.damageLocked) return 0;
    const node = nodeInBlast(this.chains, x, y, radius);
    if (!node) return 0;
    const dmg = damage * (node.head ? HEAD_DMG_MUL : BODY_DMG_MUL);
    this.markAoe(node.x, node.y, node.head ? HEAD_RADIUS : SEG_RADIUS);
    const dealt = Math.min(node.hp, dmg);
    node.hp -= dmg;
    this.hitFlash = 0.08;
    this.processDeaths();
    return dealt;
  }

  override takeDamage(amount: number): boolean {
    this.applyAoe(this.x, this.y, Infinity, amount);
    return this.dead;
  }

  /** Soft cores first: nearest body node, else a lone head (X7). */
  override aimPoint(px: number, py: number): { x: number; y: number } {
    return nearestCore(this.chains, px, py) ?? this;
  }

  /** Remove dead nodes; every surviving run of nodes becomes its own chain. */
  private processDeaths(): void {
    const next: Chain[] = [];
    for (const chain of this.chains) {
      const runs: Node[][] = [];
      let run: Node[] = [];
      for (const node of chain.nodes) {
        if (node.hp > 0) {
          run.push(node);
        } else {
          node.sprite.visible = false;
          this.events?.burst(
            node.x, node.y, node.head ? BOSS_FEEDBACK.centipedeHead : BOSS_FEEDBACK.centipedeNode,
          );
          if (run.length > 0) runs.push(run);
          run = [];
        }
      }
      if (run.length > 0) runs.push(run);
      runs.forEach((nodes, i) => {
        if (i === 0 && nodes[0] === chain.nodes[0]) {
          chain.nodes = nodes;
          next.push(chain);
        } else {
          // Rear half grows a head and strikes out on its own
          next.push(this.makeChain(nodes, chain.phase + 2.2, chain.dir === 1 ? -1 : 1));
          const h = nodes[0]!;
          this.events?.burst(h.x, h.y, BOSS_FEEDBACK.centipedeSplit);
        }
      });
    }
    this.chains = next;
    this.syncHp();
  }

  override touchesPlayer(px: number, py: number, playerRadius: number): boolean {
    return this.chains.some((c) => c.state !== 'under' &&
      c.nodes.some((n) => {
        const r = n.head ? HEAD_RADIUS : SEG_RADIUS;
        return Math.hypot(n.x - px, n.y - py) < r + playerRadius;
      }),
    );
  }

  override render(alpha: number): void {
    this.view.position.set(0, 0);
    this.drawMounds();
    for (const chain of this.chains) {
      const enraged = chain.nodes.length <= this.totalNodes / 3;
      // Underground: a faint silhouette; surfacing fades back in
      const fade = chain.state === 'under' ? BURROW.alpha
        : chain.state === 'erupt' ? 1 - (1 - BURROW.alpha) * (chain.stateT / BURROW.erupt) : 1;
      chain.nodes.forEach((node, i) => {
        node.sprite.alpha = fade;
        node.sprite.position.set(
          node.prevX + (node.x - node.prevX) * alpha,
          node.prevY + (node.y - node.prevY) * alpha,
        );
        if (node.head) {
          node.sprite.rotation = Math.atan2(chain.vy, chain.vx);
          node.sprite.texture = getTexture('boss/centipede/head', enraged ? 1 : 0);
          // In-fiction burrow tell: the head flashes red as it digs in
          node.sprite.tint =
            chain.state === 'dig' && Math.sin(this.time * 40) > 0 ? 0xff6655 : this.levelTint;
        } else {
          const prev = chain.nodes[i - 1]!;
          node.sprite.rotation = Math.atan2(node.y - prev.y, node.x - prev.x);
          node.sprite.tint = this.levelTint;
        }
      });
    }
  }

  /** Dust mound + glowing cracks where an underground chain will erupt. */
  private drawMounds(): void {
    const g = this.dirt;
    g.clear();
    for (const c of this.chains) {
      if (c.state !== 'under') continue;
      const p = Math.min(1, c.stateT / BURROW.minUnder);
      const r = BURROW.moundR * (0.5 + 0.5 * p);
      g.circle(c.tx, c.ty, r).fill({ color: MOUND, alpha: 0.5 });
      g.circle(c.tx, c.ty, r).stroke({ color: COLORS.terraCrack, width: 2 + 3 * p, alpha: 0.4 + 0.5 * p });
      const head = c.nodes[0]!;
      g.circle(head.x, head.y, BURROW.moundR * 0.4).fill({ color: MOUND, alpha: 0.35 });
    }
  }
}

/** Churned-earth brown of the burrow mounds */
const MOUND = 0x5a3a22;
